import { SearchConsoleActions, ExtendedUserActions } from 'actions'
import { Analytics } from 'common-frontend'
import { Button, Notice, HelpEmail } from 'components'
import { API_BASE } from 'consts'

import SearchConsole from './SearchConsole'

export default class SearchConsoleComplex extends React.Component {
  static contextTypes = {
    currentUser: React.PropTypes.object,
    muiTheme:    React.PropTypes.object.isRequired
  }

  static propTypes = {
    searchAnalyticsQuery: React.PropTypes.object,
    consoleStatus: React.PropTypes.any,
    consoleSetupError: React.PropTypes.object,
    legacyConsoleUser: React.PropTypes.bool
  }

  constructor(props) {
    super(props)

    this.state = {
      fetchingSearchAnalyticsQuery: true,
      searchAnalyticsQueryError: null
    }
  }

  fetchSearchAnalyticsQuery() {
    const fetcher = this.props.legacyConsoleUser ?
      SearchConsoleActions.searchAnalyticsQueryLegacy :
      SearchConsoleActions.searchAnalyticsQuery

    this.setState({ searchAnalyticsQueryError: null, fetchingSearchAnalyticsQuery: true })

    fetcher()
      .catch((searchAnalyticsQueryError) => this.setState({ searchAnalyticsQueryError }))
      .finally(() => { this.setState({ fetchingSearchAnalyticsQuery: false }) })
  }

  setupSearchConsole() {
    Analytics.track('Started Search Console Connection', {
      email: this.context.currentUser.email,
    })

    const googlePage = API_BASE + '/search_console/connection'
    window.location.href = googlePage;   
  }

  componentDidMount() {
    this.shouldRenderSearchConsole() && this.fetchSearchAnalyticsQuery()
  }

  componentWillReceiveProps(nextProps) {
    const oldSetupStatus = this.props.consoleStatus && this.props.consoleStatus.setup
    const newSetupStatus = nextProps.consoleStatus && nextProps.consoleStatus.setup

    if (newSetupStatus && !oldSetupStatus) {
      Analytics.track('Connected to Search Console', {
        email: this.context.currentUser.email,
      })

      if (nextProps.legacyConsoleUser) {
        ExtendedUserActions.update({search_console_site: null})
      } else {
        this.fetchSearchAnalyticsQuery()
      }
    }

    if (nextProps.consoleSetupError && !this.props.consoleSetupError) {
      Analytics.track('Search Console Connection Failed', {
        email: this.context.currentUser.email,
        type: nextProps.consoleSetupError.reason || 'unknown',
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.legacyConsoleUser && !this.props.legacyConsoleUser) {
      this.fetchSearchAnalyticsQuery()
    }
  }

  shouldRenderSearchConsole() {
    return this.props.legacyConsoleUser || (this.props.consoleStatus && this.props.consoleStatus.setup)
  }

  maybeRenderSearchConsoleSetup() {
    const { consoleStatus, consoleSetupError, legacyConsoleUser } = this.props
    const { palette } = this.context.muiTheme

    if (legacyConsoleUser || consoleStatus === undefined || consoleStatus.setup) return null
    if (consoleSetupError) return this.renderConsoleSetupError(consoleSetupError)

    if (consoleStatus.reason === 'revoked') {
      return (
        <Notice
          info
          header="Reconnect your shop to Google Search Console."
          className="xlarge-spacing2--bottom"
        >
          <p>Plug in SEO has been removed as an owner and disconnected from your Search Console. To track key search metrics and trends inside the app you can reconnect anytime.</p>
          <p className="medium-spacing2--top">*By pressing 'Reconnect to Search Console' you are giving Plug in Useful permission to verify the ownership of your site. This will not affect your current access and you can remove our ownership at any time.</p>
          <div className="medium-spacing--top">
            <Button
              label="Reconnect to Search Console"
              buttonType="primary"
              onTouchTap={::this.setupSearchConsole}
            />
          </div>
        </Notice>
      )
    } else {
      return (
        <Notice
          header="Connect your shop to Google Search Console"
          className="xlarge-spacing2--bottom"
        >
          <p className="body2">Track your SEO trends and get key insights by integrating Plug in SEO with Google Search Console.</p>
          <p className="medium-spacing2--top caption">*By pressing 'Connect to Search Console' you are giving Plug in SEO permission to verify the ownership of your site. This will not affect your current access and you can remove our ownership at any time.</p>
          <div className="medium-spacing--top">
            <Button
              label="Connect to Search Console"
              buttonType="primary"
              onTouchTap={::this.setupSearchConsole}
            />
          </div>
        </Notice>
      )
    }  
  }

  renderConsoleSetupError({ reason }) {
    if (reason === 'passwordProtected') {
      return (
        <Notice
          error
          header="Cannot Connect to Search Console: Your Shop is Password-protected."
          className="xlarge-spacing2--bottom"
        >
          <p>We have detected that your shop is password protected.</p>
          <p className="small-spacing--top">In order to setup search console, you need to temporarily disable the password protection. After the search console is setup, you can re-lock your shop.</p>
          <div className="medium-spacing--top">
            <Button
              label="Try again"
              buttonType="primary"
              onTouchTap={::this.setupSearchConsole}
            />
          </div>
        </Notice>
      )
    }

    return (
      <Notice 
        error
        header="Cannot Connect to Search Console"
        className="xlarge-spacing2--bottom"
      >
        <p>An unexpected error occured, refresh to try again.</p>
        <p className="small-spacing--top">If the error persists contact us at <HelpEmail /></p>
      </Notice>
    );
  }

  maybeRenderSearchConsoleUpgrade() {
    if (!this.props.legacyConsoleUser) return null
    if (this.props.consoleSetupError) return this.renderConsoleSetupError(this.props.consoleSetupError)

    return (
      <Notice
        header="Upgrade your Search Console Integration"
        className="xlarge-spacing2--bottom"
      >
        <p className="body2">We've upgraded our Search Console integration to be more robust. Reconnect to Search Console using our upgraded integration to continue to benefit from having Search Console insights inside Plug in SEO.</p>
        <p className="medium-spacing2--top caption">*By pressing 'Upgrade and Reconnect' you are giving Plug in Useful permission to verify the ownership of your site on your behalf. This will not affect your current access and you can remove our ownership at any time.</p>
        <div className="medium-spacing--top">
          <Button
            label="Upgrade and Reconnect"
            buttonType="primary"
            onTouchTap={::this.setupSearchConsole}
          />
        </div>
      </Notice>
    )
  }

  maybeRenderSearchConsole() {
    if (!this.shouldRenderSearchConsole()) return null
    const searchConsoleSite = this.props.legacyConsoleUser ?
                            this.context.currentUser.searchConsoleSite :
                            this.props.consoleStatus['site_url']

    return this.shouldRenderSearchConsole() && (
      <div className='manageplus__row'>
        <SearchConsole
          searchAnalyticsQuery={this.props.searchAnalyticsQuery}
          searchConsoleSite={searchConsoleSite}
          onRefresh={::this.fetchSearchAnalyticsQuery}
          fetchingSearchAnalyticsQuery={this.state.fetchingSearchAnalyticsQuery}
          searchAnalyticsQueryError={this.state.searchAnalyticsQueryError}
        />
      </div>
    )
  }

  render() {
    return (
      <div>
        {this.maybeRenderSearchConsoleSetup()}
        {this.maybeRenderSearchConsoleUpgrade()}
        {this.maybeRenderSearchConsole()}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./app/components/search_console/SearchConsoleComplex.js